import { webAppUrl } from '@/lib/web-app-origin';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Native side of the Meta connect flows: open the system browser on one of
 * the /connect/* hand-off pages, and refresh the relevant integration
 * queries when the user comes back to the app.
 *
 * Two return signals, and every one of them refetches:
 * - `browserFinished` — the in-app browser (SFSafariViewController / Custom
 *   Tab) was dismissed. This is the reliable signal on iOS, where the
 *   in-app browser does NOT background the app process, so an OS `resume`
 *   may never fire at all. It is also the only signal that ENDS the
 *   hand-off and removes the listeners.
 * - `resume` — covers whatever does background the app (switching to an
 *   external browser, the app switcher) in case `browserFinished` is missed.
 *   It refetches but keeps listening: on Android a `resume` can fire
 *   mid-flow (app switcher, a call, a notification tap) while the customer
 *   is still in the Custom Tab, and treating that as "done" used to remove
 *   both listeners so the real `browserFinished` minutes later was ignored
 *   and the app sat on "not connected" until a reload. A refetch is
 *   idempotent, so one extra is free; a missed one is the bug.
 *
 * Listeners from a hand-off that never saw `browserFinished` (external
 * browser path) are torn down when the next hand-off starts, so they never
 * accumulate.
 */
let teardownPreviousHandoff: (() => Promise<void>) | null = null;

/** Test-only: drop any hand-off left listening by a previous test. */
export async function resetConnectHandoffForTests(): Promise<void> {
  const teardown = teardownPreviousHandoff;
  teardownPreviousHandoff = null;
  await teardown?.();
}

export async function openConnectHandoff(input: {
  path: string;
  queryClient: QueryClient;
  queryKey: readonly unknown[];
  /** Shown in the failure toast: "Could not open the browser to connect X". */
  providerLabel: string;
}): Promise<void> {
  const { path, queryClient, queryKey, providerLabel } = input;
  let listeningForReturn = false;
  let settled = false;
  const listenerCleanups = new Set<() => Promise<void>>();

  const trackListener = async (
    listenerPromise: Promise<{ remove: () => Promise<void> }>
  ) => {
    const listener = await listenerPromise;
    const cleanup = () => listener.remove();

    // A different listener can settle the hand-off while this registration is
    // still resolving. Never add a handle after teardown has already run.
    if (settled) {
      await cleanup();
      return;
    }

    listenerCleanups.add(cleanup);
  };

  const cleanupListeners = async () => {
    const cleanups = [...listenerCleanups];
    listenerCleanups.clear();
    await Promise.allSettled(cleanups.map((cleanup) => cleanup()));
  };

  const refresh = () => {
    // Ignore stale native events delivered while the new hand-off is only
    // being prepared.
    if (!listeningForReturn || settled) return;
    void queryClient.invalidateQueries({ queryKey });
  };

  const finish = () => {
    if (!listeningForReturn || settled) return;
    settled = true;
    teardownPreviousHandoff = null;
    void cleanupListeners();
    void queryClient.invalidateQueries({ queryKey });
  };

  // A previous hand-off that only ever saw `resume` is still listening;
  // this one supersedes it.
  await teardownPreviousHandoff?.();
  teardownPreviousHandoff = async () => {
    settled = true;
    await cleanupListeners();
  };

  try {
    await trackListener(Browser.addListener('browserFinished', finish));
    await trackListener(App.addListener('resume', refresh));

    listeningForReturn = true;
    await Browser.open({ url: webAppUrl(path) });
  } catch {
    settled = true;
    teardownPreviousHandoff = null;
    await cleanupListeners();
    toast.error(
      `Could not open the browser to connect ${providerLabel} — please try again`
    );
  }
}
